import katex from 'katex';

type MixedSegment =
  | { type: 'text'; content: string }
  | { type: 'math'; content: string; displayMode: boolean };

type ItalicSegment = { content: string; italic: boolean };

const WORD_CHAR_REGEX = /[A-Za-z0-9]/;

function isEscaped(text: string, index: number): boolean {
  let slashCount = 0;
  for (let i = index - 1; i >= 0 && text[i] === '\\'; i -= 1) {
    slashCount += 1;
  }
  return slashCount % 2 === 1;
}

function isWordChar(char: string | undefined): boolean {
  return Boolean(char && WORD_CHAR_REGEX.test(char));
}

function findNextItalicToken(text: string, fromIndex: number): {
  start: number;
  end: number;
  content: string;
} | null {
  for (let start = fromIndex; start < text.length; start += 1) {
    const delimiter = text[start];
    if (delimiter !== '*' && delimiter !== '_') continue;
    if (isEscaped(text, start)) continue;

    const beforeOpen = start > 0 ? text[start - 1] : undefined;
    const afterOpen = start + 1 < text.length ? text[start + 1] : undefined;

    // Ignore list markers like "* item", and avoid pairing "**bold**" as italic.
    if (!afterOpen || /\s/.test(afterOpen) || afterOpen === delimiter || beforeOpen === delimiter) {
      continue;
    }

    // Avoid treating snake_case style underscores as italic delimiters.
    if (delimiter === '_' && isWordChar(beforeOpen) && isWordChar(afterOpen)) {
      continue;
    }

    for (let end = start + 1; end < text.length; end += 1) {
      if (text[end] !== delimiter || isEscaped(text, end)) continue;

      const beforeClose = text[end - 1];
      const afterClose = end + 1 < text.length ? text[end + 1] : undefined;
      const content = text.slice(start + 1, end);

      if (!content || content.includes('\n') || /^\s|\s$/.test(content)) continue;
      if (/\s/.test(beforeClose) || beforeClose === delimiter || afterClose === delimiter) continue;
      if (delimiter === '_' && isWordChar(beforeClose) && isWordChar(afterClose)) continue;

      return { start, end, content };
    }
  }

  return null;
}

function parseInlineItalicSegments(text: string): ItalicSegment[] {
  const segments: ItalicSegment[] = [];
  let cursor = 0;

  while (cursor < text.length) {
    const token = findNextItalicToken(text, cursor);
    if (!token) {
      segments.push({ content: text.slice(cursor), italic: false });
      break;
    }

    if (token.start > cursor) {
      segments.push({ content: text.slice(cursor, token.start), italic: false });
    }

    segments.push({ content: token.content, italic: true });
    cursor = token.end + 1;
  }

  return segments.filter((segment) => segment.content.length > 0);
}

function splitTextAndMathSegments(line: string): MixedSegment[] {
  const segments: MixedSegment[] = [];
  let cursor = 0;

  while (cursor < line.length) {
    const nextDollar = line.indexOf('$', cursor);
    if (nextDollar === -1) {
      if (cursor < line.length) {
        segments.push({ type: 'text', content: line.slice(cursor) });
      }
      break;
    }

    if (nextDollar > cursor) {
      segments.push({ type: 'text', content: line.slice(cursor, nextDollar) });
    }

    const isDisplayMath = line[nextDollar + 1] === '$';
    const mathStart = nextDollar + (isDisplayMath ? 2 : 1);
    const closingIndex = isDisplayMath
      ? line.indexOf('$$', mathStart)
      : line.indexOf('$', mathStart);

    if (closingIndex === -1) {
      segments.push({ type: 'text', content: line.slice(nextDollar) });
      break;
    }

    segments.push({
      type: 'math',
      content: line.slice(mathStart, closingIndex),
      displayMode: isDisplayMath,
    });

    cursor = closingIndex + (isDisplayMath ? 2 : 1);
  }

  if (segments.length === 0) {
    segments.push({ type: 'text', content: line });
  }

  return segments;
}

function renderMathSegment(tex: string, displayMode: boolean): string {
  try {
    const forceDisplay = !displayMode && /\\begin\s*\{array\}/.test(tex);
    return katex.renderToString(tex.trim(), {
      displayMode: displayMode || forceDisplay,
      throwOnError: false,
    });
  } catch {
    return displayMode ? `$$${tex}$$` : `$${tex}$`;
  }
}

function renderTextWithInlineItalics(text: string): string {
  return parseInlineItalicSegments(text)
    .map((segment) => (segment.italic ? `<em>${segment.content}</em>` : segment.content))
    .join('');
}

function appendTextWithInlineItalics(container: HTMLElement, text: string): void {
  parseInlineItalicSegments(text).forEach((segment) => {
    if (segment.italic) {
      const em = document.createElement('em');
      em.textContent = segment.content;
      container.appendChild(em);
      return;
    }

    container.appendChild(document.createTextNode(segment.content));
  });
}

export function normalizeLineBreaks(text: string): string {
  return text.replace(/\\n/g, '\n').replace(/\r\n?/g, '\n');
}

export function isLikelyHtmlFragment(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  return /<\/?[a-z][\w:-]*\b[^>]*>/i.test(trimmed);
}

export function renderMixedContent(text: string): string {
  const normalizedText = normalizeLineBreaks(text);

  // If the entire content is a single multiline $...$ or $$...$$ block, render it
  // as one unit. Splitting by \n first would break the closing delimiter detection.
  if (normalizedText.includes('\n')) {
    const trimmed = normalizedText.trim();
    const isDisplayBlock = trimmed.startsWith('$$') && trimmed.endsWith('$$') && trimmed.slice(2, -2).indexOf('$$') === -1;
    const isInlineBlock = !isDisplayBlock && trimmed.startsWith('$') && trimmed.endsWith('$') && trimmed.slice(1, -1).indexOf('$') === -1;
    if (isDisplayBlock || isInlineBlock) {
      const inner = isDisplayBlock ? trimmed.slice(2, -2) : trimmed.slice(1, -1);
      return renderMathSegment(inner, true);
    }
  }

  const lines = normalizedText.split('\n');
  return lines.map(renderMixedContentLine).join('<br/>');
}

export function renderMixedContentLine(line: string): string {
  return splitTextAndMathSegments(line)
    .map((segment) => {
      if (segment.type === 'math') {
        return renderMathSegment(segment.content, segment.displayMode);
      }
      return renderTextWithInlineItalics(segment.content);
    })
    .join('');
}

export function appendTextWithInlineItalicsAndLineBreaks(container: HTMLElement, text: string): void {
  const lines = normalizeLineBreaks(text).split('\n');

  lines.forEach((line, index) => {
    appendTextWithInlineItalics(container, line);
    if (index < lines.length - 1) {
      container.appendChild(document.createElement('br'));
    }
  });
}

export function appendMixedTextNodes(container: HTMLElement, text: string): void {
  const lines = normalizeLineBreaks(text).split('\n');

  lines.forEach((line, lineIndex) => {
    splitTextAndMathSegments(line).forEach((segment) => {
      if (segment.type === 'math') {
        const mathSpan = document.createElement('span');
        katex.render(segment.content, mathSpan, {
          throwOnError: false,
          displayMode: segment.displayMode,
        });
        container.appendChild(mathSpan);
        return;
      }

      appendTextWithInlineItalics(container, segment.content);
    });

    if (lineIndex < lines.length - 1) {
      container.appendChild(document.createElement('br'));
    }
  });
}
